import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { UIMemoObject, UIMemoObjectItem } from '../model/UiShapes';
import { handleError } from '../hooks/managers/errorHandler';

// MemoObject状态接口
export interface MemoObjectState {
  // State
  memoObjects: UIMemoObject[];
  currentMemoObject: UIMemoObject | null;
  status: 'idle' | 'loading' | 'failed';
  isSyncing: boolean;
  error: string | null;
  searchObjects: string[];
  selectedObjects: string[];
  isLoading: boolean;

  // Actions
  setMemoObjects: (objects: UIMemoObject[]) => void;
  addMemoObject: (object: UIMemoObject) => void;
  updateMemoObject: (memoId: string, objects: any[]) => void;
  setCurrentMemoObjectByMemoId: (memoId: string) => void;
  setCurrentMemoObject: (object: UIMemoObject | null) => void;
  forceRefreshMemoObjectByMemoId: (memoObject: UIMemoObject | null) => void;
  setStatus: (status: 'idle' | 'loading' | 'failed') => void;
  setIsSyncing: (syncing: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  clear: () => void;
  setLoading: (loading: boolean) => void;

  // Object filter actions
  setSearchObjects: (objects: string[]) => void;
  setSelectedObjects: (objects: string[]) => void;
  addSelectedObject: (object: string) => void;
  removeSelectedObject: (object: string) => void;
}

// 创建memoObjectStore
export const useMemoObjectStore = create<MemoObjectState>()(
  devtools((set, get) => ({
    // 初始状态
    memoObjects: [],
    currentMemoObject: null,
    status: 'idle',
    isSyncing: false,
    error: null,
    searchObjects: [],
    selectedObjects: [],
    isLoading: false,

    // Actions
    setMemoObjects: (objects: UIMemoObject[]) => {
      // 确保objects始终是数组类型
      if (!Array.isArray(objects)) {
        handleError(new Error('setMemoObjects: 接收到非数组类型'), { context: 'memoObjectStore', showToast: false });
        set({ memoObjects: [] });
      } else {
        set({ memoObjects: objects });
      }
    },

    addMemoObject: (object: UIMemoObject) =>
      set((state) => ({
        memoObjects: [...state.memoObjects.filter((mo) => mo.memoId !== object.memoId), object]
      })),

    updateMemoObject: (memoId: string, objects: any[]) => {
      set((state) => {
        const updatedMemoObject: UIMemoObject = {
          memoId: memoId,
          objects: objects
        };

        return {
          memoObjects: [...state.memoObjects.filter((memoObject: UIMemoObject) => memoObject.memoId !== memoId), updatedMemoObject],
          currentMemoObject: updatedMemoObject
        };
      });
    },

    setCurrentMemoObject: (object: UIMemoObject | null) => {
      set({ currentMemoObject: object });
    },

    setCurrentMemoObjectByMemoId: (memoId: string) => {
      // 缓存优先策略：首先检查store缓存中是否已有该memo的objects
      const existingObject = get().memoObjects.find((object: UIMemoObject) => object.memoId === memoId);

      if (existingObject) {
        set({ currentMemoObject: existingObject });
      } else {
        // 找不到对应对象时返回null
        set({ currentMemoObject: null });
      }
    },

    forceRefreshMemoObjectByMemoId: (memoObject: UIMemoObject | null) => {
      if (memoObject) {
        // 更新store状态
        get().addMemoObject(memoObject);
        set({ status: 'idle', error: null });
      } else {
        // 清除对应的缓存 - 当memoObject为null时，不需要清除特定memo的缓存
        set({ status: 'idle', error: null });
      }
    },

    setStatus: (status: 'idle' | 'loading' | 'failed') => set({ status }),

    setIsSyncing: (syncing: boolean) => set({ isSyncing: syncing }),

    setError: (error: string | null) => set({ error }),

    clearError: () => set({ error: null }),

    clear: () => {
      set({
        memoObjects: [],
        currentMemoObject: null,
        status: 'idle',
        isSyncing: false,
        error: null,
        searchObjects: [],
        selectedObjects: [],
        isLoading: false
      });
    },

    // Object filter actions
    setSearchObjects: (objects: string[]) => set({ searchObjects: objects }),

    setSelectedObjects: (objects: string[]) => set({ selectedObjects: objects }),

    addSelectedObject: (object: string) =>
      set((state) => ({
        selectedObjects: state.selectedObjects.includes(object) ? state.selectedObjects : [...state.selectedObjects, object]
      })),

    removeSelectedObject: (object: string) =>
      set((state) => ({
        selectedObjects: state.selectedObjects.filter((o) => o !== object)
      })),

    setLoading: (loading: boolean) => set({ isLoading: loading })
  }))
);
